import Cookies from 'js-cookie';

import { getBooleanConfig, getStringListConfig } from '@zg-rentals/frontend-config/client';

import {
  ZG_COMP_INSIGHTS,
  ZRM_AUTO_SPOTLIGHT_UI_ALLOWLIST_EMAILS,
  ZRM_AUTO_SPOTLIGHT_UI_ENABLED,
  ZRM_FEATURE_TOUR_ENABLED,
  ZRM_FEED_OVERRIDES_ENABLED,
  ZRM_FLEXIBLE_PROPERTIES_ENABLED,
  ZRM_MEDIA_CREDITS_ENABLED,
  ZRM_MULTIPLEX_LISTING_FLOW_ENABLED,
  ZRM_OWNERSHIP_VERIFICATION_ENABLED,
  ZRM_SHOW_CANCELLED_TOURS,
  ZRM_SMART_ASSIGN_UI_ALLOWLIST_EMAILS,
  ZRM_SMART_ASSIGN_UI_ENABLED,
  ZRM_TOTAL_MONTHLY_PRICE_ENABLED,
  ZRM_USER_MANAGEMENT_ENABLED,
  ZRM_USER_ONBOARDING_ENABLED,
} from '../constants/FeatureFlagConstants';
import SplitTestConstants from '../constants/SplitTestConstants';
import { USER_ONBOARDING_DISMISSED_KEY } from '../constants/UserOnboardingConstants';

import { getCookieAsBoolean } from './cookieUtils';
import propertyUtils from './propertyUtils';

import type { ValueOf } from 'type-fest';
import type { SplitExperimentsProps } from '@zg-rentals/split';
import type {
  CometUserRoleType,
  GetUserPropertyDetailsListingTypeWebDto,
  GetUserPropertyDetailsResponseWebDto,
  GetUserPropertySpotlightOverviewResponseDto,
  RentalManagerAuthCheckUserInfoWebApiDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const canAccessModularListing = ({
  isMultifamily,
  listingTypeCode,
  lastActivatedMs,
  userCanAccessModularListing,
}: {
  isMultifamily: boolean;
  listingTypeCode: number;
  lastActivatedMs: number | undefined;
  userCanAccessModularListing: boolean;
}): boolean => {
  const hasListingBeenActivated = Boolean(lastActivatedMs);
  const isSingleFamilyRental = !isMultifamily && listingTypeCode === 1;
  const isRoomForRent = listingTypeCode === 5;

  return !hasListingBeenActivated && (isSingleFamilyRental || isRoomForRent) && userCanAccessModularListing;
};

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}): boolean => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({
  lastActivatedMs,
  isFeed,
  isMultifamily,
}: {
  lastActivatedMs?: number;
  isFeed?: boolean;
  isMultifamily?: boolean;
}) => {
  return Boolean((lastActivatedMs || isFeed) && !isMultifamily);
};

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const canAccessFastPost = ({
  isMultifamily,
  lastActivatedMs,
  isFeed,
  restrictions,
}: {
  isMultifamily: boolean;
  lastActivatedMs: number | undefined;
  isFeed: boolean;
  restrictions: { applications: { isAccessRestricted: boolean } };
}) => {
  return !isMultifamily && !lastActivatedMs && !isFeed && !restrictions?.applications?.isAccessRestricted;
};

const canAccessPostingPathTourScheduling = ({
  isFeed,
  isInReactivationFlow,
  canAccessRelisting,
  active,
}: {
  isFeed: boolean;
  isInReactivationFlow: boolean;
  canAccessRelisting: boolean;
  active: boolean;
}) => {
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({
  active,
  isFeed,
  isMultifamily,
  listingType,
}: Partial<{
  active: boolean;
  isFeed: boolean;
  isMultifamily: boolean;
  listingType: GetUserPropertyDetailsListingTypeWebDto;
}>) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ listing: { listingType } });

  return !active && !isRoomForRent && !isFeed && !isMultifamily;
};

const canAccessTotalMonthlyPriceWithFee = ({ isEligible = false }: { isEligible?: boolean }) =>
  getCookieAsBoolean(ZRM_TOTAL_MONTHLY_PRICE_ENABLED) || isEligible;

const isTourSchedulingEnabledForProperty = ({
  active,
  canBeRelisted,
  isFeed,
  isInReactivationFlow,
}: {
  active: boolean;
  canBeRelisted: boolean;
  isFeed: boolean;
  isInReactivationFlow: boolean;
}) => {
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(!isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessUserOnboardingExistingUser = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }) => {
  if (!user) return false;

  if (!user?.restrictions?.isAllowed) {
    return false;
  }

  if (typeof window !== 'undefined') {
    try {
      if (sessionStorage.getItem(USER_ONBOARDING_DISMISSED_KEY) === 'true') {
        return false;
      }
    } catch {
      // Silently fail if sessionStorage is unavailable (e.g., restricted browser context)
    }
  }

  const hasListings = (user?.summary?.totalListingCount ?? 0) > 0;

  if (getCookieAsBoolean(ZRM_USER_ONBOARDING_ENABLED)) {
    return hasListings;
  }

  return Boolean(
    hasListings &&
    (!user.firstName ||
      !user.lastName ||
      !user.profiles?.profileType ||
      !user.profiles?.propertyCountRange ||
      !user?.firstName?.trim() ||
      !user?.lastName?.trim()),
  );
};

const canAccessUserOnboardingNewUser = ({
  user,
}: {
  user: {
    restrictions?: { isAllowed: boolean };
    features: { canViewOnboardingFlow: boolean };
  } | null;
}) => {
  if (user?.restrictions?.isAllowed && user.features.canViewOnboardingFlow) {
    return true;
  }

  return false;
};

const canAccessMultiplexListingFlow = ({
  canAccessMultiplexListingFlowResponse,
}: {
  canAccessMultiplexListingFlowResponse: boolean;
}) => {
  const areRequiredConditionsSatisfied = canAccessMultiplexListingFlowResponse === true;
  const areOverrideConditionsSatisfied = getCookieAsBoolean(ZRM_MULTIPLEX_LISTING_FLOW_ENABLED);

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const shouldShowBrokerFeeAlert = ({ state }: { state: string }) => {
  return state ? state === 'MA' : false;
};

const canFeedConnectSwitchSource = ({ user }: { user: { features: { canSwitchListingSource: boolean } } | null }) =>
  user?.features?.canSwitchListingSource;

const canAccessAddFeedListingsModal = ({ user }: { user: { features: { canViewClaimFeedListing: boolean } } | null }) =>
  user?.features?.canViewClaimFeedListing;

const canAccessFeatureNotifications = ({
  user,
}: {
  user: { features: { canAccessFeatureLaunchNotifications: boolean } } | null;
}) => user?.features?.canAccessFeatureLaunchNotifications;

const isNonAdvertiser = ({ user }: { user: { roleTypes: Array<CometUserRoleType> } | null }) =>
  Boolean(user?.roleTypes?.includes('NON_ADVERTISER_CUSTOMER' as CometUserRoleType));

const canAccessClaimPath = ({
  cookie,
  hasAnyPropertiesToClaim,
  isScreenMobile,
  isNonAdvertiser: isNonAdvertiserValue,
}: {
  cookie?: string;
  hasAnyPropertiesToClaim: boolean;
  isScreenMobile: boolean;
  isNonAdvertiser: boolean;
}) =>
  !isScreenMobile && (hasAnyPropertiesToClaim || isNonAdvertiserValue) && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessClaimMorePath = ({
  isScreenMobile,
  hasAnyClaimedListings,
  hasNewPropertiesToClaim,
  cookie,
}: {
  isScreenMobile: boolean;
  hasAnyClaimedListings: boolean;
  hasNewPropertiesToClaim: boolean;
  cookie: string;
}) =>
  !isScreenMobile && hasAnyClaimedListings && hasNewPropertiesToClaim && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessSpotlightListingsFirstUseModal = ({
  isScreenMobile,
  isSpotlightListingsEligible,
  cookie,
}: {
  isScreenMobile: boolean;
  isSpotlightListingsEligible: boolean;
  appConfig?: unknown;
  cookieEnabledServices?: unknown;
  cookie?: string;
}) => Boolean(!isScreenMobile && isSpotlightListingsEligible && (!cookie || Cookies.get(cookie) !== 'true'));

const canAccessListingSpotlightFilter = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}) => Boolean(user?.isSingleFamilyEnterpriseCustomer && spotlightOverview?.isEligible);

const isAutoSpotlightAllowlistedEmail = (email?: string | null): boolean => {
  if (!email) {
    return false;
  }
  const normalizedEmail = email.toLowerCase();
  const allowlist = [
    ...ZRM_AUTO_SPOTLIGHT_UI_ALLOWLIST_EMAILS,
    ...getStringListConfig('zrmAutoSpotlightUiAllowlistEmails', []),
  ];
  return allowlist.some((allowed) => allowed.trim().toLowerCase() === normalizedEmail);
};

// Only customers with an active Listing Spotlight contract can set the optimization mode. hasSpotlightedListings
// reflects the contract; pinned/auto/available are all 0 for fully-used contracts predating the engine.
const hasActiveSpotlightContract = (spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null): boolean =>
  Boolean(spotlightOverview?.isEligible) && Boolean(spotlightOverview?.hasSpotlightedListings);

const canAccessAutoSpotlightUi = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}): boolean =>
  (getCookieAsBoolean(ZRM_AUTO_SPOTLIGHT_UI_ENABLED) || isAutoSpotlightAllowlistedEmail(user?.email)) &&
  hasActiveSpotlightContract(spotlightOverview);

const isSmartAssignAllowlistedEmail = (email?: string | null): boolean => {
  if (!email) {
    return false;
  }
  const normalizedEmail = email.toLowerCase();
  const allowlist = [
    ...ZRM_SMART_ASSIGN_UI_ALLOWLIST_EMAILS,
    ...getStringListConfig('zrmSmartAssignUiAllowlistEmails', []),
  ];
  return allowlist.some((allowed) => allowed.trim().toLowerCase() === normalizedEmail);
};

// Separate from the panel gate: pilot customers already see the panel, and the Smart Assign controls stay dark
// until product says they can ship. Allowlisted emails get in without the cookie.
const canAccessSmartAssignUi = ({ user }: { user: RentalManagerAuthCheckUserInfoWebApiDto | null }): boolean =>
  getCookieAsBoolean(ZRM_SMART_ASSIGN_UI_ENABLED) || isSmartAssignAllowlistedEmail(user?.email);

const canAccessSmartAssignPanel = ({
  user,
  spotlightOverview,
}: {
  user: RentalManagerAuthCheckUserInfoWebApiDto | null;
  spotlightOverview?: GetUserPropertySpotlightOverviewResponseDto | null;
}): boolean => canAccessSmartAssignUi({ user }) && hasActiveSpotlightContract(spotlightOverview);

const canAccessPortfolioSection = (): boolean => {
  return getBooleanConfig('canAccessUserManagement', false) || getCookieAsBoolean(ZRM_USER_MANAGEMENT_ENABLED);
};

type PossibleFeatureName = ValueOf<typeof SplitTestConstants>;
const getSplitExperimentTreatment = (
  splitExperiments: SplitExperimentsProps = {},
  featureName: PossibleFeatureName | '' = '',
) => splitExperiments?.[featureName]?.treatment === 'on';

const canAccessFlexibleProperties = ({
  splitExperiments,
}: {
  splitExperiments?: SplitExperimentsProps;
} = {}): boolean => {
  const isSplitTreatmentOn = getSplitExperimentTreatment(
    splitExperiments,
    SplitTestConstants.ZRM_FLEXIBLE_PROPERTY_CREATION,
  );

  // On localhost the appConfig is not available, so treat it as enabled.
  const isDevelopment = process.env.NODE_ENV === 'development';
  const appConfig = isDevelopment || getBooleanConfig('canAccessFlexibleProperties', false);

  const requiredConditionsSatisfied = appConfig && isSplitTreatmentOn;
  const overrideConditionsSatisfied = getCookieAsBoolean(ZRM_FLEXIBLE_PROPERTIES_ENABLED);

  return requiredConditionsSatisfied || overrideConditionsSatisfied;
};

const canAccessFeatureTour = (): boolean => {
  return getCookieAsBoolean(ZRM_FEATURE_TOUR_ENABLED);
};

const canAccessCondensedFlow = ({
  canAccessRelistingFlow,
  canAccessMLReactivationFlow,
  lastActivatedMs,
}: {
  canAccessRelistingFlow?: boolean;
  canAccessMLReactivationFlow?: boolean;
  lastActivatedMs?: number;
}): boolean => !canAccessRelistingFlow && !canAccessMLReactivationFlow && !lastActivatedMs;

const canAccessFeedOverrides = (): boolean =>
  getBooleanConfig('canAccessFeedOverrides', false) || getCookieAsBoolean(ZRM_FEED_OVERRIDES_ENABLED);

const canAccessMediaCredits = (): boolean =>
  getBooleanConfig('canAccessMediaCredits', false) || getCookieAsBoolean(ZRM_MEDIA_CREDITS_ENABLED);

const canAccessAIListingCreationTestB = ({
  canAccessRelistingFlow,
  canAccessMLReactivationFlow,
  lastActivatedMs,
}: {
  canAccessRelistingFlow?: boolean;
  canAccessMLReactivationFlow?: boolean;
  lastActivatedMs?: number;
}): boolean => !canAccessRelistingFlow && !canAccessMLReactivationFlow && !lastActivatedMs;

/** Rollout gate only; whether a given listing needs verification is a separate server call. */
const canAccessOwnershipVerification = (): boolean =>
  getBooleanConfig('canAccessOwnershipVerification', false) || getCookieAsBoolean(ZRM_OWNERSHIP_VERIFICATION_ENABLED);

const canAccessCompetitiveInsights = (): boolean => getCookieAsBoolean(ZG_COMP_INSIGHTS);

/** Whether the tour calendar renders cancelled tours; must match the tour-calendar page's own gate. */
const canShowCancelledTours = (): boolean =>
  getBooleanConfig('canShowCancelledTours', false) || getCookieAsBoolean(ZRM_SHOW_CANCELLED_TOURS);

/** Self-serve onboarding blocks photo changes. Unknown details read as unrestricted; see useIsPhotoUploadRestricted. */
const isPhotoUploadRestricted = (listingDetails: GetUserPropertyDetailsResponseWebDto | undefined) =>
  Boolean(
    listingDetails?.selfServeOnboardingStatus?.isSelfServeOnboardingInProgress &&
    listingDetails?.selfServeOnboardingStatus?.isUpdateRestricted,
  );

export default {
  canAccessModularListing,
  canAccessTopOfFunnelPremium,
  canAccessMLReactivationFlow,
  canAccessSFCostsAndFees,
  canAccessFastPost,
  canAccessPostingPathTourScheduling,
  canAccessTotalMonthlyPriceWithFee,
  canAccessMultiplexListingFlow,
  canAccessRelistingFlow,
  canAccessListingPreview,
  canAccessUserOnboardingNewUser,
  canAccessUserOnboardingExistingUser,
  isTourSchedulingEnabledForProperty,
  shouldShowBrokerFeeAlert,
  canFeedConnectSwitchSource,
  canAccessAddFeedListingsModal,
  canAccessFeatureNotifications,
  isNonAdvertiser,
  canAccessClaimPath,
  canAccessClaimMorePath,
  canAccessSpotlightListingsFirstUseModal,
  canAccessListingSpotlightFilter,
  canAccessAutoSpotlightUi,
  canAccessSmartAssignPanel,
  canAccessSmartAssignUi,
  canAccessCompetitiveInsights,
  canShowCancelledTours,
  canAccessPortfolioSection,
  canAccessFlexibleProperties,
  canAccessFeatureTour,
  canAccessFeedOverrides,
  canAccessMediaCredits,
  canAccessCondensedFlow,
  canAccessAIListingCreationTestB,
  canAccessOwnershipVerification,
  isPhotoUploadRestricted,
};
